import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useHideMobileTabBar } from './MobileChrome';
import { MobileBackButton, MobileHeader } from './MobileHeader';
import { RecordOpener } from './RecordOpener';

type PanelState = { mobilePanel?: string } & Record<string, unknown>;

/**
 * A screen pushed over the current page on phones without changing the URL
 * (conversation details, a team chat's info). Opening it adds a history
 * entry, so the iOS edge swipe, the browser Back button and the in-app back
 * button all close it, and the page underneath stays mounted with its scroll
 * position, filters and unsent text.
 *
 * The open state lives in history, so rotating to a wider layout and back
 * brings the same panel back.
 */
export function useMobilePanel(id: string) {
	const location = useLocation();
	const navigate = useNavigate();
	const state = (location.state ?? {}) as PanelState;
	const isOpen = state.mobilePanel === id;
	const url = location.pathname + location.search + location.hash;

	const open = useCallback(() => {
		if (isOpen) return;
		navigate(url, { state: { ...state, mobilePanel: id } });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, url, id, navigate, location.state]);

	const close = useCallback(() => {
		if (!isOpen) return;
		const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
		if (idx > 0) navigate(-1);
		else navigate(url, { replace: true, state: { ...state, mobilePanel: undefined } });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, url, navigate, location.state]);

	return { isOpen, open, close };
}

interface MobilePushedPageProps {
	open: boolean;
	onClose: () => void;
	/** Names the screen in the header and for VoiceOver. */
	title: string;
	subtitle?: ReactNode;
	/** Header trailing slot (MobileHeaderButtons). */
	right?: ReactNode;
	/** Text after the back chevron, e.g. the screen underneath. */
	backLabel?: string;
	/** Replaces the default header entirely. */
	header?: ReactNode;
	/** Pinned under the scroll area, e.g. a StickyActionBar or composer. */
	footer?: ReactNode;
	children: ReactNode;
	className?: string;
	bodyClassName?: string;
	/**
	 * Where focus goes on close when the control that opened the page has gone
	 * (a rotation swapped the layout underneath): a selector or a function.
	 * Defaults to the screen underneath's back button, else its title.
	 */
	returnFocusTo?: string | (() => HTMLElement | null | undefined);
}

const TABBABLE =
	'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable]:not([contenteditable="false"]), [tabindex]';

// The first and last elements Tab can reach inside the page.
function tabEdges(page: HTMLElement) {
	const items = Array.from(page.querySelectorAll<HTMLElement>(TABBABLE)).filter(
		(el) => el.tabIndex >= 0 && !el.closest('[inert]') && el.getClientRects().length > 0,
	);
	return { first: items[0], last: items[items.length - 1] };
}

const isShown = (el: Element) => el.isConnected && el.getClientRects().length > 0 && !el.closest('[inert]');

// The screen underneath's back button, else its title (focusable without a
// ring until focus moves on), else its first header control. A root screen's
// header holds actions (Export, Select, New), which Enter would trigger.
function screenUnderneath(): HTMLElement | null {
	const screen = document.querySelector<HTMLElement>('[data-app-page]');
	if (!screen) return null;
	const header = Array.from(screen.querySelectorAll('header')).find(isShown);
	const controls = header
		? Array.from(header.querySelectorAll<HTMLElement>(TABBABLE)).filter((el) => el.tabIndex >= 0 && isShown(el))
		: [];
	const back = controls.find((el) => /^Back( to |$)/.test(el.getAttribute('aria-label') ?? ''));
	if (back) return back;
	const heading = Array.from(screen.querySelectorAll<HTMLElement>('h1, h2, [role="heading"]')).find(isShown);
	if (!heading) return controls[0] ?? null;
	if (!heading.hasAttribute('tabindex')) {
		heading.tabIndex = -1;
		heading.style.outline = 'none';
		const reset = () => {
			heading.removeAttribute('tabindex');
			heading.style.outline = '';
			if (!heading.getAttribute('style')) heading.removeAttribute('style');
		};
		heading.addEventListener('blur', reset, { once: true });
	}
	return heading;
}

const panelInHistory = () => (window.history.state as { usr?: PanelState } | null)?.usr?.mobilePanel;

// Openers of pages that went away while still open in history, for when the
// same page comes back (a rotation round trip that kept the opener).
const parkedOpeners = new Map<string, HTMLElement>();

/**
 * The full-screen phone screen that goes with useMobilePanel. It slides in
 * from the right, hides the tab bar, and renders only while open. Pops are
 * not animated: iOS already animates its swipe-back.
 *
 * Keyboard focus stays on the page: Tab wraps at its edges, and focus that
 * lands in the covered app underneath (#root) comes back. Sheets, dialogs and
 * popovers opened from the page are portalled next to it, so they keep their
 * own focus handling, and so do toasts.
 *
 * Closing hands focus back to what opened the page. When that has gone (the
 * page was open across a rotation that swapped the screen underneath), focus
 * goes to `returnFocusTo`, else the screen's back button or title. A page that
 * goes away while it is still open in history (rotating wider) leaves focus
 * alone: it comes back when the layout does.
 */
export function MobilePushedPage({
	open,
	onClose,
	title,
	subtitle,
	right,
	backLabel,
	header,
	footer,
	children,
	className,
	bodyClassName,
	returnFocusTo,
}: MobilePushedPageProps) {
	useHideMobileTabBar(open);
	const ref = useRef<HTMLDivElement>(null);
	const returnFocus = useRef<HTMLElement | null>(null);
	// Latest props for the effect, so a new onClose doesn't re-run it and
	// bounce focus out of a field on the page.
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	const returnFocusToRef = useRef(returnFocusTo);
	returnFocusToRef.current = returnFocusTo;

	useEffect(() => {
		if (!open) return;
		const page = ref.current;
		const openedAs = panelInHistory();
		if (openedAs !== undefined) {
			returnFocus.current ??= parkedOpeners.get(openedAs) ?? null;
			parkedOpeners.delete(openedAs);
		}
		page?.focus({ preventScroll: true });
		const appRoot = document.getElementById('root');
		// Shift+Tab that escapes anyway comes back to the last element.
		let backwards = false;
		const onKey = (event: KeyboardEvent) => {
			if (event.defaultPrevented) return;
			if (event.key === 'Escape') onCloseRef.current();
			if (event.key !== 'Tab') return;
			backwards = event.shiftKey;
			const page = ref.current;
			const active = document.activeElement;
			const lost = !active || active === document.body;
			if (!page || (!lost && !page.contains(active))) return;
			const { first, last } = tabEdges(page);
			if (!first) {
				event.preventDefault();
			} else if (lost) {
				event.preventDefault();
				(event.shiftKey ? last : first).focus();
			} else if (event.shiftKey && (active === first || active === page)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		};
		const onFocusIn = (event: FocusEvent) => {
			const page = ref.current;
			const target = event.target as HTMLElement | null;
			if (!page || !target || !appRoot?.contains(target) || target.closest('[data-sonner-toaster]')) return;
			const { first, last } = tabEdges(page);
			((backwards ? last : first) ?? page).focus({ preventScroll: true });
		};
		const onKeyUp = () => {
			backwards = false;
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('keyup', onKeyUp);
		document.addEventListener('focusin', onFocusIn);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('keyup', onKeyUp);
			document.removeEventListener('focusin', onFocusIn);
			const opener = returnFocus.current;
			returnFocus.current = null;
			if (openedAs !== undefined && panelInHistory() === openedAs) {
				if (opener) parkedOpeners.set(openedAs, opener);
				return;
			}
			// Leave focus alone if something else already took it (a field on the next screen).
			const active = document.activeElement;
			if (active && active !== document.body && !page?.contains(active)) return;
			const to = returnFocusToRef.current;
			const targets = [
				() => opener,
				() => (typeof to === 'string' ? document.querySelector<HTMLElement>(to) : to?.()),
				screenUnderneath,
			];
			for (const target of targets) {
				const el = target();
				if (!el || !isShown(el) || page?.contains(el)) continue;
				el.focus({ preventScroll: true });
				if (document.activeElement === el) return;
			}
		};
	}, [open]);

	if (!open) return null;
	return createPortal(
		<div
			ref={ref}
			role="dialog"
			aria-modal="true"
			aria-label={title}
			tabIndex={-1}
			className={cn(
				// Starts below the in-call bar (--call-bar-height) like the shell does.
				'fixed inset-x-0 top-[var(--call-bar-height,0px)] z-40 flex h-[calc(var(--app-height)-var(--call-bar-height,0px))] flex-col bg-background pt-safe outline-none',
				'duration-300 ease-out animate-in slide-in-from-right motion-reduce:animate-none',
				className,
			)}
		>
			{/* Notes the opener before the page or a field in it takes focus; a tap on iOS doesn't focus the button. */}
			<RecordOpener into={returnFocus} />
			{header ?? (
				<MobileHeader
					always
					title={title}
					subtitle={subtitle}
					left={<MobileBackButton onClick={onClose} label={backLabel} />}
					right={right}
				/>
			)}
			<div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', !footer && 'pb-safe kb-open:pb-0', bodyClassName)}>
				{children}
			</div>
			{footer}
		</div>,
		document.body,
	);
}
